import { useCallback, useEffect, useRef, useState } from "react";

// Minimal typings for the Web Speech API (not in lib.dom.d.ts). We only touch
// the handful of members we use; results are read defensively.
interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onspeechend: (() => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

// Safety cap: if neither a result, speechend, nor end ever fires (seen on iOS),
// force the session to abort so the microphone is always released.
const MAX_LISTEN_MS = 15_000;

// On iOS, every third-party browser (Chrome=CriOS, Firefox=FxiOS, Edge=EdgiOS,
// Opera=OPiOS, the Google app=GSA, …) is forced onto Apple's WKWebView, where
// `webkitSpeechRecognition` exists but is broken: it turns the mic on yet never
// returns a result or fires `onend`, so the session hangs and the mic light
// stays on. Only Safari has a working implementation. Treat those WebViews as
// unsupported so we don't show a mic that can only get stuck.
function isUnsupportedSpeechEnv(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS =
    /iP(hone|od|ad)/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return isIOS && /(CriOS|FxiOS|EdgiOS|OPiOS|mercury|GSA)/.test(ua);
}

function getCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined" || isUnsupportedSpeechEnv()) return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Browser-native speech-to-text dictation (Web Speech API). On-device, no
 * backend/API key. `supported` is false where the API is missing (e.g.
 * Firefox); callers should hide the mic then. `onText` receives final
 * transcript chunks to insert into the composer.
 *
 * iOS Safari is the tricky one: with `continuous = false` it still does NOT
 * reliably auto-stop after a final result and leaves the mic open, and it may
 * not fire `onend`. So we end the session explicitly (on result + on
 * speechend), abort if a stop() is ignored, and keep a hard watchdog so the mic
 * is guaranteed to release.
 */
export function useDictation({ lang, onText }: { lang: string; onText: (text: string) => void }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ctor = getCtor();
  const supported = Boolean(ctor);

  // Latest onText without rebuilding the recognizer.
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const finish = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
    setListening(false);
    recRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (!rec) return;
    try {
      rec.stop();
    } catch {
      /* ignore */
    }
    // iOS sometimes ignores stop() / never fires onend → force-release shortly after.
    setTimeout(() => {
      if (recRef.current === rec) {
        try {
          rec.abort();
        } catch {
          /* ignore */
        }
        finish();
      }
    }, 800);
  }, [finish]);

  const start = useCallback(() => {
    if (!ctor) return;
    const rec = new ctor();
    rec.lang = lang === "zh" ? "zh-CN" : "en-US";
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e) => {
      let text = "";
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (r.isFinal) text += r[0].transcript;
      }
      text = text.trim();
      if (text) onTextRef.current(text);
      // End the session as soon as we have the phrase; iOS won't on its own.
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    };
    // Speech detected as ended (silence) → stop so the mic releases promptly.
    rec.onspeechend = () => {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    };
    rec.onend = finish;
    rec.onerror = finish;
    recRef.current = rec;
    setListening(true);
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(() => {
      try {
        rec.abort();
      } catch {
        /* ignore */
      }
      finish();
    }, MAX_LISTEN_MS);
    try {
      rec.start();
    } catch {
      finish(); // e.g. start() called twice
    }
  }, [ctor, lang, finish]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  // Abort any in-flight recognition on unmount.
  useEffect(
    () => () => {
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      recRef.current?.abort();
    },
    []
  );

  return { supported, listening, toggle };
}
